import { Component, Input, ViewChild, OnDestroy, AfterViewInit, OnInit, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators, AbstractControl } from "@angular/forms";
import { Subscription, of, Observable, forkJoin, BehaviorSubject, from } from "rxjs";
import { filter, concatMap, tap, map, switchMap } from "rxjs/operators";
//import { GridOptions, ColDef, RedrawRowsParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, RedrawRowsParams, GridApi } from "ag-grid-community";

import { Well, EditedFormationRecord, GISPointTypes, WellPermittingModifiers, WellLocation, Elevation, Depths, GISPoint, EditedCasingTubingRecord, WellProductionReporting } from '../../common/models/well.model';
import { TabsComponent } from '../../common/components/tabs/tabs.component';
import { TabComponent } from '../../common/components/tabs/tab.component';
import { dateComparator } from '../../common/components/data-table/comparators/data-table.comparators';
import { DomainDefinitionService } from '../../builder/services/domain-definition.service';
import { GridFormatterService } from '../../common/services/grid-formatter.service';
import { DomainValue } from '../../models/domain.model';
import { AgGridAngular } from "ag-grid-angular";
import { FinancialAssuranceService } from '../../common/services/financial-assurance.service';
import { FinancialInstrument } from '../../common/models/financialAssurance.model';
import { IssuesService } from '../../common/services/issues.service';
import { ComplianceReportSearchSpec, ComplianceReportStatus } from '../../issues/models/compliance.model';
import { PostDrillingConfig } from "../../post-drilling/models/post-drilling.models";
import { ApplicationConfigService, ModuleConfig } from "../../services/application-config.service";
import { StratigraphicWellBondingRequirement } from "../../common/models/well.model";
import { GisQueryService } from "../../gis/services/gis-query.service";
import { GisLocationService } from "../../gis/services/gis-location.service";
import { GisTokenService } from "../../gis/services/gis-token.service";
import { LoaderService } from "../../common/services/loader.service";
import { AuthService } from "../../services/auth.service";

import { Organization } from '../../common/models/organization.model';

import { DynamicDialogService } from "../../core/services/dynamic-dialog.service";
import { Dialog, DialogControl, DialogButton, DialogControlType, DialogOptionSet, PropertyDependency, PreventBooleanFromChanging } from "../../core/models/dialog.model";
import { Option } from "../../common/models/flow.dto";
import { WellDetailFormationsComponent } from './well-detail-formations/well-detail-formations.component';
import { WellDetailCasingStringsComponent } from './well-detail-casing-strings/well-detail-casing-strings.component';
import { ComplianceDatesComponent } from './compliance-dates/compliance-dates.component';
import { WellService } from "../../common/services/well.service";

@Component({
    selector: 'well-details',
    templateUrl: './well-details.component.html',
    styleUrls: ['./well-details.component.scss'],
    providers: [GisQueryService, GisTokenService, GisLocationService],
    standalone: false
})
export class WellDetailsComponent implements AfterViewInit, OnInit, OnDestroy {

    @ViewChild(AgGridAngular, { static: true }) grid: AgGridAngular;
    @ViewChild(TabsComponent, { static: true }) public tabs: TabsComponent;
	@ViewChild(WellDetailFormationsComponent) public formationsComponent: WellDetailFormationsComponent;
	@ViewChild(WellDetailCasingStringsComponent) public casingsComponent: WellDetailCasingStringsComponent;
    @ViewChild(ComplianceDatesComponent) complianceDates: ComplianceDatesComponent;

    @Output() formEdited: EventEmitter<boolean> = new EventEmitter<boolean>();

    public get allowSwitchToggling(): boolean {
        if (this._well == null || this._well.wellStatus == null) return false;
        return this._well.wellStatus.type.description == "XR";
    }

	@Input() activeOrganization: Organization;
    @Input() canEditWellDetails: boolean = false;
    @Input() canEditWellTypeDescription: boolean = false;
	@Input() canEditWellDispositionLegacyTracking: boolean = false;

	private restrictedWellTypes: string[] = ['GS_R', 'BR_R', 'ER_R', 'IW_R', 'MN_R', 'SM_R', 'SW_R', 'LS_R'];

	get canSeeDocuments(): boolean {
		return this.showDocuments && (this._auth.User.isInternal || this.isWellOwner || this.restrictedWellTypes.includes(this.well.wellStatus.type.type) == false);
	}

	get canSeeComplianceDates(): boolean {
		return this._auth.User.isInternal || this.isWellOwner;
	}

	@Output() casingRecordUpdated: EventEmitter<EditedCasingTubingRecord> = new EventEmitter<EditedCasingTubingRecord>();

	private _configLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	get configLoaded(): boolean {
		return this._configLoaded.value;
	}


    editorDialog: Dialog = new Dialog("<div class=\"bold\">Update Well Details</div><div class=\"text-align-left\">Comments will be temporarily stored with the change until the change is saved using the \"Save\" button.  When the change is saved, comments will appear under the \"Well Notes\" tab.</div>", [new DialogButton("Save", true, (args) => this.saveUpdate(args)), new DialogButton("Cancel", false, () => { return; })], true, []);
    editorForm: FormGroup = new FormGroup({});
    private _enableEditing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    get enableEditing(): boolean {
        return this._enableEditing.value;
    }

    get enableFullEditor(): boolean {
        if (this.postDrillingConfig == null) return false;
        return this.postDrillingConfig.wellDetailsEditing && this.canEditWellDetails;
	}

	@Output() formationRecordUpdated: EventEmitter<EditedFormationRecord> = new EventEmitter<EditedFormationRecord>();

    get infoBubble(): string {
        if (this.editorForm == null) return "";
        let typeForm = this.editorForm.get('wellType');
        if (typeForm == null) return "";
        return "Changing to / from Domestic Well Type Descriptions ('XR', 'XO', 'XB') will affect other flags on the well to ensure they read correctly as either domestic or non-domestic as appropriate.";
	}

	get isWellOwner(): boolean {
		if (this._well == null) return false;
		return this.activeOrganization != null && (this._well.organizationId == this.activeOrganization.id || (this._well.historicalOwnerNumber != null && this.activeOrganization.historicalOwnerNumber != null && this._well.historicalOwnerNumber.length > 0 && this._well.historicalOwnerNumber == this.activeOrganization.historicalOwnerNumber));
	}

    //Defaulting these to true for now per https://dev.azure.com/jmtdevelopers/RICS/_workitems/edit/35287/
    displayWellTypeDescription: boolean = true;
    displayWellDispositionLegacyTracking: boolean = true;

    public hasReleaseOfLease: boolean = false;
    public isWithout5Acres: boolean = true;
    public hasFetchedFinancialInstrument: boolean = false;

    @Input() showDocuments: boolean = false;

    viewDocuments: boolean = false;

    @Input() wellTypeDomainValues: DomainValue[];
    @Input() wellDispositionDomainValues: DomainValue[];

    private _well: Well;
    private _wellFinancialInstrument: FinancialInstrument;
    private _wellTypeDescriptionDomainId: string;
    private _wellDispositionLegacyTrackingDomainId: string;
    private _dispositionOptions: DialogOptionSet[] = [];
    private _typeDescriptionOptions: DialogOptionSet[] = [];
    private _releaseOfLeaseOptions: DialogOptionSet[] = [];
    private _without5AcresOptions: DialogOptionSet[] = [];


	gridOptions: GridOptions;
	gridApi?: GridApi;
	modifiedCasingRecords: EditedCasingTubingRecord[] = [];
	modifiedFormationRecords: EditedFormationRecord[] = [];


    pendingBondingRequirement: StratigraphicWellBondingRequirement;

    postDrillingConfig: PostDrillingConfig;

	public complianceDatesEditorForm: FormGroup;


    subscriptions: Subscription = new Subscription();

    well$: Observable<any> = this.activatedRoute.data.pipe(tap((result: any) => {
        if (result.well) {
            this.well = result.well;
            if (this.well.wellStatus) {

                if (this.well.wellStatus.type) {
                    if (this.well.wellStatus.type.description || this._wellTypeDescriptionDomainId != null)
                        this.displayWellTypeDescription = true;
                }

                if (this.well.wellStatus.legacyTracking || this._wellDispositionLegacyTrackingDomainId != null)
                    this.displayWellDispositionLegacyTracking = true;
            }
        }

        return result.well;
    }));

    wellAssociatedFinancialInstrument$: Observable<FinancialInstrument | null> = this.well$.pipe(concatMap((well) => {
        if (well != null) {
            return this.financialAssuranceService.getWellActiveFinancialInstrument(well.well.id);
        }

        return of(null);
    }));

    wellDispositionLegacyTrackingDomainValues$: Observable<any> = this.well$.pipe(concatMap((well) => {
        if (well && this.well.wellStatus) {
            if (this.well.wellStatus.disposition) {
                //display either the legacyTracking value itself, or the dropdown, if one exists, or is possible.
                if (this.well.wellStatus.legacyTracking || this._wellDispositionLegacyTrackingDomainId != null)
                    this.displayWellDispositionLegacyTracking = true;

                if (this._wellDispositionLegacyTrackingDomainId != null) {
                    return this.domainService.getValues(this._wellDispositionLegacyTrackingDomainId);
                }
            }
        }

        return of(null);
    }));

    wellTypeDescriptionDomainValues$: Observable<any> = this.well$.pipe(concatMap((well) => {
        if (well && this.well.wellStatus) {
            if (this.well.wellStatus.type) {
                //display either the description itself, or the dropdown, if one exists, or is possible.
                if (this.well.wellStatus.type.description || this._wellTypeDescriptionDomainId != null)
                    this.displayWellTypeDescription = true;

                if (this._wellTypeDescriptionDomainId != null) {
                    return this.domainService.getValues(this._wellTypeDescriptionDomainId);
                }
            }
        }

        return of(null);
    }));

    



    wellTypeDescriptionDomainValues: DomainValue[];
    wellDispositionLegacyTrackingDomainValues: DomainValue[];

    get apiNumber(): string {
        if (this._well) {
            return this.well.id;
        } else {
            return "";
        }
    }

    get depths(): Depths {
        if (this.well == null) return null;
        return this.well.depths;
    }

    get displayDomesticCheckboxes(): boolean {
        if (this._well) {
            return this._well.wellStatus.type.description == "XR" || this._well.wellStatus.type.description == "XB";
        } else {
            return false;
        }
    }

    get elevation(): Elevation {
        if (this.location == null) return null;
        return this.location.elevation;
    }

    get enableDetailEditing(): boolean {
        if (this.postDrillingConfig == null) return false;
        return this.postDrillingConfig.wellDetailsEditing;
	}

    get isPermittedUrban() {
        if (!this._well || !this._well.wellStatus || !this._well.wellStatus.permittingModifiers) return;
        return (this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ConsideredUrbanForPermitting) > 0;
    }

    get leaseName(): string {
        if (this.well == null || this.well.lease == null) return "";
        return this.well.lease.name;
    }

    get location(): WellLocation {
        if (this.well == null) return null;
        return this.well.location;
    }

    get well() {
        return this._well;
    }

    @Input() set well(well: Well) {
        this._well = well;

        if (this._well) {
            this.buildEditorForm();
            this.setDomesticWellFlags();
        }
    }

    private get _disposition(): string {
        if (this._well == null || this._well.wellStatus == null) return null;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let dispositionForm: FormGroup = <FormGroup> this.editorForm.get('disposition');
                if (dispositionForm != null) {
                    let disposition = dispositionForm.get('disposition');
                    if (disposition != null) return disposition.value;
                }
            }
        }
        return this._well.wellStatus.disposition;
    }

    private get _legacyTracking(): string {
        if (this._well == null || this._well.wellStatus == null) return null;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let dispositionForm: FormGroup = <FormGroup>this.editorForm.get('disposition');
                if (dispositionForm != null) {
                    let legacyTracking = dispositionForm.get("legacyTracking");
                    if (legacyTracking != null) return legacyTracking.value;
                }
            }
        }
        return this._well.wellStatus.legacyTracking;
    }

    get legacyTracking(): string {
        let legacy = "";
        if (this._legacyTracking && this._dispositionOptions != null && this._dispositionOptions.length > 0) {
            let optionSet = this._dispositionOptions.find(o => o.value === this._disposition);
            if (optionSet != null) {
                let option = optionSet.set.find(o => o.id === this._legacyTracking);
                if (option != null) legacy = option.text;
            }
        }
        return legacy;
    }

    get has5Acres(): boolean {
        if (this._well == null) return false;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let typeForm: FormGroup = <FormGroup>this.editorForm.get("wellType");
                if (typeForm != null) {
                    let has5 = typeForm.get('has5Acres');
                    if (has5 != null) return has5.value;
                }
            }
        }
        return (this._well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) == 0;
    }

    get releaseOfLease(): boolean {
        if (this._well == null) return false;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let typeForm: FormGroup = <FormGroup>this.editorForm.get("wellType");
                if (typeForm != null) {
                    let hasRelease = typeForm.get('hasReleaseOfLease');
                    if (hasRelease != null) return hasRelease.value;
                }
            }
        }
        return (this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0;
    }

    get wellDisposition(): string {
        let wellDisposition = "";

        if (this._disposition) {

            if (this.wellDispositionDomainValues && this.wellDispositionDomainValues.length > 0) {
                let matchingDomainValue = this.wellDispositionDomainValues.filter(a => a.Code == this._disposition);
                if (matchingDomainValue.length > 0)
                    wellDisposition = matchingDomainValue[0].Definition;
                else
                    wellDisposition = this._well.wellStatus.disposition;
            }
            else
                wellDisposition = this._well.wellStatus.disposition;
        }

        return wellDisposition;
    }

    private get _wellType(): string {
        if (this._well == null || this._well.wellStatus == null) return null;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let typeForm: FormGroup = <FormGroup>this.editorForm.get('wellType');
                if (typeForm != null) {
                    let wellType = typeForm.get("wellType");
                    if (wellType != null) return wellType.value;
                }
            }
        }
        return this._well.wellStatus.type.type;
    }

    get wellStatusType(): string {
        let type: string = "";
        if (this._wellType) {
            if (this.wellTypeDomainValues && this.wellTypeDomainValues.length > 0) {
                let matchingDomainValue = this.wellTypeDomainValues.filter(a => a.Code == this._wellType);
                if (matchingDomainValue.length > 0) {
                    type = matchingDomainValue[0].Definition;
                } else {
                    type = this._well.wellStatus.type.type;
                }
            } else {
                type = this._well.wellStatus.type.type;
            }
        }

        return type;
    }

    private get _wellTypeDescription(): string {
        if (this._well == null || this._well.wellStatus == null) return null;
        if (this.enableDetailEditing) {
            if (this.editorForm != null) {
                let typeForm: FormGroup = <FormGroup>this.editorForm.get('wellType');
                if (typeForm != null) {
                    let description = typeForm.get("wellTypeDescription");
                    if (description != null) return description.value;
                }
            }
        }
        return this._well.wellStatus.type.description;
    }

    get wellStatusTypeDescription(): string {
        let description = "";
        if (this._wellTypeDescription && this._typeDescriptionOptions != null && this._typeDescriptionOptions.length > 0) {
            let optionSet = this._typeDescriptionOptions.find(o => o.value === this._wellType);
            if (optionSet != null) {
                let option = optionSet.set.find(o => o.id === this._wellTypeDescription);
                if (option != null) description = option.text;
            }
        }
        return description;
    }

    get wellDispositionLegacyTracking(): string {
        let wellDispositionLegacyTracking = "";

        if (this._well && this._well.wellStatus && this._well.wellStatus.legacyTracking) {

            if (this.wellDispositionLegacyTrackingDomainValues && this.wellDispositionLegacyTrackingDomainValues.length > 0) {
                let matchingDomainValue = this.wellDispositionLegacyTrackingDomainValues.filter(a => a.Code == this.well.wellStatus.legacyTracking);
                if (matchingDomainValue.length > 0)
                    wellDispositionLegacyTracking = matchingDomainValue[0].Definition;
                else
                    wellDispositionLegacyTracking = this._well.wellStatus.legacyTracking;
            }
            else
                wellDispositionLegacyTracking = this._well.wellStatus.legacyTracking;
        }

        return wellDispositionLegacyTracking;
    }

    @Input() set wellDispositionLegacyTrackingDomainId(wellDispositionLegacyTrackingDomainId: string) {
        if (this._wellDispositionLegacyTrackingDomainId != wellDispositionLegacyTrackingDomainId) {
            this._wellDispositionLegacyTrackingDomainId = wellDispositionLegacyTrackingDomainId;

            if (this._wellDispositionLegacyTrackingDomainId)
                this.loadWellDispositionLegacyTrackingData();
        }
    }

    get wellFinancialInstrumentNumber() {
        if (this._wellFinancialInstrument && this._wellFinancialInstrument.instrumentId)
            return this._wellFinancialInstrument.instrumentId;
        else {
            return this.hasFetchedFinancialInstrument ? "N/A" : "Loading...";
        }
    }

    get wellType(): string {
        let wellType = "";

        if (this._well && this._well.wellStatus && this._well.wellStatus.type) {

            if (this.well.wellStatus.type.type && this.wellTypeDomainValues && this.wellTypeDomainValues.length > 0) {
                let matchingDomainValue = this.wellTypeDomainValues.filter(a => a.Code == this.well.wellStatus.type.type);
                if (matchingDomainValue.length > 0)
                    wellType = matchingDomainValue[0].Definition;
                else
                    wellType = this._well.wellStatus.type.type;
            }
            else
                wellType = this._well.wellStatus.type.type;
        }

        return wellType;
    }


    get wellTypeDescription(): string {
        let wellTypeDescription = "";

        if (this._well && this._well.wellStatus && this._well.wellStatus.type) {

            if (this.well.wellStatus.type.description && this.wellTypeDescriptionDomainValues && this.wellTypeDescriptionDomainValues.length > 0) {
                let matchingDomainValue = this.wellTypeDescriptionDomainValues.filter(a => a.Code == this.well.wellStatus.type.description);
                if (matchingDomainValue.length > 0)
                    wellTypeDescription = matchingDomainValue[0].Definition;
                else
                    wellTypeDescription = this._well.wellStatus.type.description;
            }
            else
                wellTypeDescription = this._well.wellStatus.type.description;
        }

        return wellTypeDescription;
    }

    @Input() set wellTypeDescriptionDomainId(wellTypeDescriptionDomainId: string) {

        if (this._wellTypeDescriptionDomainId != wellTypeDescriptionDomainId) {
            this._wellTypeDescriptionDomainId = wellTypeDescriptionDomainId;

            if (this._wellTypeDescriptionDomainId)
                this.loadWellTypeDescriptionData();
        }
    }

    public validPointTypes: GISPointTypes[] = [
        GISPointTypes.WellHead,
        GISPointTypes.Target,
        GISPointTypes.Heel,
        GISPointTypes.Toe
    ];



    get sourceOfLocation(): string {
        let value = this._well.location.sourceOfLocation;
        if (this.enableDetailEditing && this.editorForm != null) {
            let locationForm: FormGroup = <FormGroup>this.editorForm.get('location');
            if (locationForm != null) {
                let location = locationForm.get('location');
                if (location != null) value = location.value;
            }
        }
        if (this.locationSources != null && this.locationSources.length > 0) {
            let match = this.locationSources.find(f => f.Code == value);
            if (match != null) {
                value = match.Definition
            }
        }
        return value;
    }

    get sourceOfLocationOptions(): Option[] {
        if (this.locationSources == null) return null;
        let options: Option[] = [];
        this.locationSources.forEach(l => {
            let option: Option = {
                id: l.Code,
                text: l.Definition
            };
            options.push(option);
        });
        return options;
    }

	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agTotalAndFilteredRowCountComponent',
				align: 'right',
			}
		]
	};


    private locationSources: DomainValue[] = [];

    constructor(public activatedRoute: ActivatedRoute,
        public domainService: DomainDefinitionService,
        public formatterService: GridFormatterService,
        public financialAssuranceService: FinancialAssuranceService,
        public issuesService: IssuesService,
        private router: Router,
        public config: ApplicationConfigService,
        public dialogSvc: DynamicDialogService,
        private builder: FormBuilder,
        public readonly locationService: GisLocationService,
		public readonly queryService: GisQueryService,
		private readonly _auth: AuthService,
		private readonly _wellService: WellService,
        private readonly loader: LoaderService) {
    }

   

    ngOnInit() {
		this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.buildColumns());
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.gridApi.sizeColumnsToFit();
        };
		
		
        this.subscriptions.add(this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        }));

        let config$: Subscription = this.config.getModuleConfig().pipe(
            tap((conf: ModuleConfig) => {
				if (conf != null) {
					this.postDrillingConfig = conf.postDrilling;
					this._configLoaded.next(true);
				}
            }),
            switchMap(() => {
                if (this.postDrillingConfig != null && this.postDrillingConfig.wellDetailsEditing) {
                    return this.getDispositionLegacyTrackingOptions();
                } else {
                    return of([]);
                }
            }),
            switchMap(() => {
                if (this.postDrillingConfig != null && this.postDrillingConfig.wellDetailsEditing) {
                    return this.getWellTypeDescriptionOptions();
                } else {
                    return of([]);
                }
            })
            )
            .subscribe(
                () => { this._enableEditing.next(true); },
                (error) => { console.error(error); }
        );
        this.subscriptions.add(config$);

        //Fetch the well type domain values, if applicable
        let wellTypes$: Observable<DomainValue[]> = (this.wellTypeDomainValues != null && this.wellTypeDomainValues.length > 0)
            ? of(this.wellTypeDomainValues)
            : this.domainService.getValues("WELL_TYP").pipe(
                tap((data: DomainValue[]) => {
                    if (data != null) {
                        this.wellTypeDomainValues = data;
                        data.forEach(t => {
                            let leaseOption: DialogOptionSet = new DialogOptionSet();
                            leaseOption.value = t.Code;
                            let acresOption: DialogOptionSet = new DialogOptionSet();
                            acresOption.value = t.Code;
                            switch (t.Code) { //reset both flags if the well type is changed
                                default:
                                    leaseOption.booleanValue = false;
                                    acresOption.booleanValue = false;
                                    break;
                                
                            }
                            this._releaseOfLeaseOptions.push(leaseOption);
                            this._without5AcresOptions.push(acresOption);
                        });                       
                    
                    }
                })
            )

        //Fetch the well Disposition domain values, if applicable
        let disposition$: Observable<DomainValue[]> = (this.wellDispositionDomainValues != null && this.wellDispositionDomainValues.length > 0) 
            ? of(this.wellDispositionDomainValues)
            : this.domainService.getValues("STATUS_DESC").pipe(
                tap((data: DomainValue[]) => {
                    if (data != null) this.wellDispositionDomainValues = data;
                })
            )
        let subs$ = forkJoin([disposition$, wellTypes$]).subscribe(
            () => { },
            (error) => {
                console.error("ERROR fetching the STATUS_DESC or WELL_TYP domain.");
                console.error(error);
            }
        )
        this.subscriptions.add(subs$);      
                
        if (this._wellDispositionLegacyTrackingDomainId == null && this._wellTypeDescriptionDomainId == null)
            this.loadWellData();
        else {
            if (this._wellDispositionLegacyTrackingDomainId != null)
                this.loadWellDispositionLegacyTrackingData();

            if (this._wellTypeDescriptionDomainId != null)
                this.loadWellTypeDescriptionData();
        }

        
        const soc$: Observable<DomainValue[]> = this.domainService.getValues("SOURCE_LOC").pipe(
            tap((data: DomainValue[]) => {
                this.locationSources = data;
                this.locationSources.unshift({
                    Code: "", Definition: ""
                } as DomainValue);
            })
        );

        let subz$: Subscription = soc$.subscribe(
            () => { },
            (error) => {
                console.error(error);
            }
        );
        this.subscriptions.add(subz$);
    }

	ngAfterViewInit() {    
		if (this.canSeeComplianceDates && this.complianceDates != null) {
			this.complianceDatesEditorForm = this.complianceDates.editorForm;
		}	
		
        this.subscriptions.add(this.tabs.tabClicked.subscribe((result: TabComponent) => {
            switch (result.title) {
                case "General Details":
                    this.grid.api.sizeColumnsToFit();
                    break;
                case "Well Documents": 
                    this.viewDocuments = true;
                    break;
               
                default:
                    break;
            }
		}));
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    addPoint(type: GISPointTypes): void {
        let form = this.editorForm.get(type);
        if (form != null) {
            let ctrl = form.get('pointAdded');
            if (ctrl != null) ctrl.setValue(true);
        }
    }

    boolToYesNo(bool: boolean) {
        return (bool && bool == true) ? "Yes" : "No";
    }

    buildColumns(): ColDef[] {
        let columns = [
            {
                headerName: 'OrganizationId',
                headerTooltip: 'OrganizationId',
                field: "organizationId",
                tooltipField: "organizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
            },
            {
                headerName: 'OrganizationName',
                headerTooltip: 'OrganizationName',
                field: "organizationName",
                tooltipField: "organizationName",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
            },
            {
                headerName: 'Date Ownership Taken',
                headerTooltip: 'Date Ownership Taken',
                field: 'dateOwnershipTaken',
                tooltipValueGetter: this.formatterService.formatDate('dateOwnershipTaken'),
                valueGetter: this.formatterService.formatDate('dateOwnershipTaken'),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
            },
            {
                headerName: 'Date Ownership Relinquished',
                headerTooltip: 'Date Ownership Relinquished',
                field: 'dateOwnershipRelinquished',
                tooltipValueGetter: this.formatterService.formatDate('dateOwnershipRelinquished'),
                valueGetter: this.formatterService.formatDate('dateOwnershipRelinquished'),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
            }
        ] as ColDef[];

        return columns;
    }

    buildEditorForm(): void {      
        let form: FormGroup = this.builder.group({
            lease: this.builder.group({ leaseName: [this.leaseName, []], comments: [null, [Validators.required]] }),
            wellSymbol: this.builder.group({ wellSymbol: [this.well.mapSymbol, []], comments: [null, [Validators.required]] }),
            acresInDrillingUnit: this.builder.group({ acresInDrillingUnit: [this.well.acresInDrillingUnit, []], comments: [null, [Validators.required]] }),
            rigType: this.builder.group({ rigType: [this.well.rigType, []], comments: [null, [Validators.required]] }),
            wellName: this.builder.group({ wellName: [this.well.name, []], comments: [null, [Validators.required]] }),
            wellNumber: this.builder.group({ wellNumber: [this.well.wellNumber, []], comments: [null, [Validators.required]] }),
            wellClass: this.builder.group({ wellClass: [this.well.odnrCurrentClass, []], comments: [null, [Validators.required]] }),
            mapSymbol: this.builder.group({ mapSymbol: [this.well.mapSym, []], comments: [null, [Validators.required]] }),
            groundElevation: this.builder.group({ groundElevation: [(this.elevation == null) ? null : this.elevation.groundLevel, []], comments: [null, [Validators.required]] }),
            dtd: this.builder.group({ dtd: [this.depths == null ? null : this.depths.drillersMeasuredVerticalDepth, []], comments: [null, [Validators.required]] }),
            targetElevation: this.builder.group({ targetElevation: [(this.elevation == null) ? null : this.elevation.target, []], comments: [null, [Validators.required]] }),
            kickoff: this.builder.group({ kickoff: [(this.depths == null) ? null : this.depths.kickoffTrueVerticalDepth, []], comments: [null, [Validators.required]] }),
            digitalElevationModel: this.builder.group({ digitalElevationModel: [(this.elevation == null) ? null : this.elevation.digitalElevationModel, []], comments: [ null, [Validators.required]] }),
            plugbackDepth: this.builder.group({ plugbackDepth: [(this.depths == null) ? null : this.depths.plugbackMeasuredDepth, []], comments: [ null, [Validators.required]] }),
            loggersDepth: this.builder.group({ loggersDepth: [(this.depths == null) ? null : this.depths.loggerMeasuredVerticalDepth, []], comments: [ null, [Validators.required]] }),
            trueVerticalDepth: this.builder.group({ trueVerticalDepth: [(this.depths == null) ? null : this.depths.trueVerticalDepth, []], comments: [ null, [Validators.required]] }),
            proposedFormation: this.builder.group({ proposedFormation: [this.well.proposedFormation, []], comments: [null, [Validators.required]] }),
            dateIssued: this.builder.group({ dateIssued: [this.well.constructionPermit.dateApproved, []], comments: [null, [Validators.required]] }),
            aapgBasinCode: this.builder.group({ aapgBasinCode: [this.well.aapgBasinCode, []], comments: [null, [Validators.required]] }),
            spudDate: this.builder.group({ spudDate: [this.well.dateWellDrillingStarted, []], comments: [null, [Validators.required]] }),
            dateOfFirstProduction: this.builder.group({ dateOfFirstProduction: [this.well.production.dateOfFirstProduction, []], comments: [null, [Validators.required]] }),
            dateTotalDepthReached: this.builder.group({ dateTotalDepthReached: [this.well.dateTotalDepthReached, []], comments: [null, [Validators.required]] }),
            datePluggingApproved: this.builder.group({ datePluggingApproved: [this.well.plugging.datePluggingPlanApproved, []], comments: [null, [Validators.required]] }),
			datePluggedAndAbandoned: this.builder.group({ datePluggedAndAbandoned: [this.well.plugging.datePluggedAndAbandoned, []], comments: [null, [Validators.required]] }),
			vaultedVented: this.builder.group({ vaultedVented: [this.well.vaultedVented, []], comments: [null, [Validators.required]] }),
            dateOfPlugBack: this.builder.group({ dateOfPlugBack: [this.well.plugging.dateOfPlugBack, []], comments: [null, [Validators.required]] }),
            disposition: this.builder.group({
                disposition: [this._disposition, []],
                legacyTracking: [this._well.wellStatus.legacyTracking, []], 
                comments: [ null, [Validators.required]]
            }),
            wellType: this.builder.group({
                wellType: [this._wellType, []],
                wellTypeDescription: [this._wellTypeDescription, []],
                has5Acres: [this.has5Acres, []],
                hasReleaseOfLease: [this.releaseOfLease, []],
                comments: [ null, [Validators.required]]
            }),
            location: this.builder.group({
                location: [this.well.location.sourceOfLocation, []],
                comments: [ null, [Validators.required]]
            }),
            wellHead: this.buildPointForm(GISPointTypes.WellHead),
            target: this.buildPointForm(GISPointTypes.Target),
            heel: this.buildPointForm(GISPointTypes.Heel),
            toe: this.buildPointForm(GISPointTypes.Toe)            
        });
        this.editorForm = form;
    }

    buildPointForm(pointType: GISPointTypes): FormGroup {
        let point = this._well.location.gisPoints.find(p => p.pointType == pointType);
        let pointForm: FormGroup = new FormGroup({});
        if (point != null) {
            pointForm = this.builder.group({
                identifier: [point.identifier, []],
                type: [pointType, []],
                pointAdded: [false, []],
                apiNumber: [this._well.id, []],
                gisQueryRun: [false, [Validators.requiredTrue]],
                county: this.builder.group({
                    apiNumber: [point.county != null ? point.county.apiNumber: null, []],
                    name: [point.county != null ? point.county.name : null, []]
                }),
                lot: [point.lot, []],
                section: [point.section, []],
                township: this.builder.group({
                    number: [point.township != null ? point.township.number : null, []],
                    direction: [point.township != null ? point.township.direction: null, []],
                    name: [point.township != null ? point.township.name : null, []]
                }),
                range: this.builder.group({
                    number: [point.range != null ? point.range.number : null, []],
                    direction: [point.range != null ? point.range.direction: null, []],
                    name: [point.range != null ? point.range.name : null, []]
                }),
                quarterTownship: [point.quarterTownship, []],
                footageCallsFromSectionLines: [point.footageCallsFromSectionLines, []],
                allotment: [point.allotment, []],
                originalSurvey: [point.originalSurvey, []],
                tract: [point.tract, []],
                fraction: [point.fraction, []],
                civilTownshipNumber: [point.civilTownshipNumber, []],
                cardinalPointSection: [point.cardinalPointSection, []],
                ogsQuadrangle: this.builder.group({
                    code: [point.ogsQuadrangle != null ? point.ogsQuadrangle.code : null, []],
                    name: [point.ogsQuadrangle != null ? point.ogsQuadrangle.name: null, []]
                }),
                coordinates: this.builder.group({
                    statePlaneZone: [point.coordinates != null ? point.coordinates.statePlaneZone : null, []],
                    statePlaneXCoordinate: [point.coordinates != null ? point.coordinates.statePlaneXCoordinate: null, []],
                    statePlaneYCoordinate: [point.coordinates != null ? point.coordinates.statePlaneYCoordinate : null, []],
                    statePlaneOriginalXCoordinate: [point.coordinates != null ? point.coordinates.statePlaneOriginalXCoordinate : null, []],
                    statePlaneOriginalYCoordinate: [point.coordinates != null ? point.coordinates.statePlaneOriginalYCoordinate : null, []],
                    latitude: [point.coordinates != null ? point.coordinates.latitude : null, []],
                    longitude: [point.coordinates != null ? point.coordinates.longitude : null, []],
                    nad83XCoordinate: [point.coordinates != null ? point.coordinates.nad83XCoordinate: null, []],
                    nad83YCoordinate: [point.coordinates != null ? point.coordinates.nad83YCoordinate: null, []],
                    longitude83: [point.coordinates != null ? point.coordinates.longitude83 : null, []],
                    latitude83: [point.coordinates != null ? point.coordinates.latitude83 : null, []]
                }),
                bearing: [point.bearing, []],
                distance: [point.distance, []],
                comments: [null, [Validators.required]]
            });
        } else {
            pointForm = this.builder.group({
                identifier: [null, []],
                type: [pointType, []],
                pointAdded: [false, []],
                apiNumber: [this._well.id, []],
                gisQueryRun: [false, [Validators.requiredTrue]],
                county: this.builder.group({
                    apiNumber: [null, []],
                    name: [null, []]
                }),
                lot: [null, []],
                section: [null, []],
                cardinalPointSection: [null, []],
                township: this.builder.group({
                    number: [null, []],
                    direction: [null, []],
                    name: [null, []]
                }),
                range: this.builder.group({
                    number: [null, []],
                    direction: [null, []],
                    name: [null, []]
                }),
                quarterTownship: [null, []],
                footageCallsFromSectionLines: [null, []],
                allotment: [null, []],
                originalSurvey: [null, []],
                tract: [null, []],
                fraction: [null, []],
                civilTownshipNumber: [null, []],
                ogsQuadrangle: this.builder.group({
                    code: [null, []],
                    name: [null, []]
                }),
                coordinates: this.builder.group({
                    statePlaneZone: [null, []],
                    statePlaneXCoordinate: [null, []],
                    statePlaneYCoordinate: [null, []],
                    statePlaneOriginalXCoordinate: [null, []],
                    statePlaneOriginalYCoordinate: [null, []],
                    latitude: [null, []],
                    longitude: [null, []],
                    nad83XCoordinate: [null, []],
                    nad83YCoordinate: [null, []],
                    longitude83: [null, []],
                    latitude83: [null, []]
                }),
                bearing: [null, []],
                distance: [null, []],
                FTCalls: [null, []],
                comments: [null, [Validators.required]]
            });
        }
        return pointForm;
    }

   

    getDispositionLegacyTrackingOptions(): Observable<DialogOptionSet[]> {
        let spudded$: Observable<DialogOptionSet> = this.domainService.getValues("WellSpuddedLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "DG";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let drilled$: Observable<DialogOptionSet> = this.domainService.getValues("WellDrilledLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "DR";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let restoration$: Observable<DialogOptionSet> = this.domainService.getValues("FinalRestorationLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "FR";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let plugged$: Observable<DialogOptionSet> = this.domainService.getValues("PluggedLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "PA";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let production$: Observable<DialogOptionSet> = this.domainService.getValues("ProductionStartedLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "PR";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let orphan$: Observable<DialogOptionSet> = this.domainService.getValues("OrphanLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "ORPH";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let unknown$: Observable<DialogOptionSet> = this.domainService.getValues("UnknownLegacyTrackingOptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "UN";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );


        return forkJoin([spudded$, drilled$, restoration$, plugged$, production$, orphan$, unknown$]).pipe(
            tap((sets: DialogOptionSet[]) => {
                this._dispositionOptions = sets;
            })
        );
    }

    getEditIcon(formName: string, subForm: string = ""): string {
        let form: FormGroup = <FormGroup>this.editorForm.get(formName);
        if (subForm != null && subForm.length > 0) {
            form = <FormGroup>form.get(subForm);
        }
        return form != null && form.dirty ? "icon-notepad-alert" : "icon-notepad";
    }

    getPointValueFromForm(point: GISPointTypes, field: string): any {
        let pointForm = this.editorForm.get(point);
        if (pointForm != null) {
            let nameParts = field.split('|');
            let ctrl: AbstractControl = null;
            for (let i = 0; i < nameParts.length; i++) {
                if (ctrl == null) ctrl = pointForm.get(nameParts[i])
                else ctrl = ctrl.get(nameParts[i]);
            }
            if (ctrl != null) return ctrl.value;
        }
        return null;
    }


	getQuadrangleDisplay(point: GISPoint): string {
		return this.pointHasQuadrangleName(point)
				? point.ogsQuadrangle.name
				: point.ogsQuadrangle.code;		
	}

	getQuadrangleDisplayForForm(point: GISPointTypes): string {
		let value = this.getPointValueFromForm(point, 'ogsQuadrangle|name');
		if (value == null || value.trim().length == 0) {
			value = this.getPointValueFromForm(point, 'ogsQuadrangle|code');
		}
		return value;
	}

	pointHasQuadrangleName(point: GISPoint): boolean {
		return point != null && point.ogsQuadrangle != null && point.ogsQuadrangle.name != null && point.ogsQuadrangle.name.length > 0;
	}


    getWellTypeDescriptionOptions(): Observable<DialogOptionSet[]> {
        let oilgas$: Observable<DialogOptionSet> = this.domainService.getValues("OilAndGasWellTypeDescriptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "OG_R";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let recovery$: Observable<DialogOptionSet> = this.domainService.getValues("EnhancedRecoveryWellTypeDescriptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "ER_R";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let disposal$: Observable<DialogOptionSet> = this.domainService.getValues("DisposalWellTypeDescriptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "SW_R";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        let unknown$: Observable<DialogOptionSet> = this.domainService.getValues("UnknownWellTypeDescriptions").pipe(
            map((data: DomainValue[]) => {
                let set = new DialogOptionSet();
                set.value = "UN_R";
                set.set = [];
                data.forEach(d => {
                    let option: Option = {
                        id: d.Code,
                        text: d.Definition
                    };
                    if (option.id.length === 0) option.id = " ";
                    set.set.push(option);
                })
                return set;
            })
        );

        return forkJoin([oilgas$, recovery$, disposal$, unknown$]).pipe(
            tap((sets: DialogOptionSet[]) => {
                this._typeDescriptionOptions = sets;
                sets.forEach(s => {
                    s.set.forEach(t => {
                        let leaseOption: DialogOptionSet = new DialogOptionSet();
                        leaseOption.value = t.id;
                        let acresOption: DialogOptionSet = new DialogOptionSet();
                        acresOption.value = t.id;
                        switch (t.id) {
                            case "XB":
                                leaseOption.booleanValue = true;
                                acresOption.booleanValue = true;
                                break;
                            case "XR":
                                leaseOption.booleanValue = false;
                                acresOption.booleanValue = true;
                                break;
                            case "XO":
                                leaseOption.booleanValue = (this.well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) != 0;
                                acresOption.booleanValue = (this.well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) == 0;
                                break;
                            default:
                                leaseOption.booleanValue = false;
                                acresOption.booleanValue = false;
                                break;
                        }
                        this._releaseOfLeaseOptions.push(leaseOption);
                        this._without5AcresOptions.push(acresOption);
                    })
                });
            })
        );
    }

    loadWellDispositionLegacyTrackingData() {
        //Fetch the well disposition legacy tracking domain values, if applicable
        if (this._wellDispositionLegacyTrackingDomainId != null) {
            this.subscriptions.add(this.wellDispositionLegacyTrackingDomainValues$.pipe(
            ).subscribe(
                (domainValues: DomainValue[]) => {
                    if (domainValues)
                        this.wellDispositionLegacyTrackingDomainValues = domainValues;
                },
                error => { console.error(error); console.error("ERROR Fetching the " + this._wellDispositionLegacyTrackingDomainId + " domain for this well."); }
            ));
        }
    }

    loadWellData() {
        this.subscriptions.add(this.well$.pipe(
        ).subscribe(
            (well) => {
                //If the well has a legacy tracking or type description field, and they can't edit the well fetch the relevant domains here.
                if (this._wellDispositionLegacyTrackingDomainId == null && (this.well && this.well.wellStatus && this.well.wellStatus.legacyTracking)) {
                    this._wellDispositionLegacyTrackingDomainId = "STATUS_REAS";
                    this.loadWellDispositionLegacyTrackingData();
                }

                if (this._wellTypeDescriptionDomainId == null && (this.well && this.well.wellStatus && this.well.wellStatus.type && this.well.wellStatus.type.description)) {
                    this._wellTypeDescriptionDomainId = "WLTYP_DESC";
                    this.loadWellTypeDescriptionData();
                }

                this.loadWellFinancialInstrument();
            },
            error => { console.error(error); console.error("ERROR Fetching the well."); }
        ));
    }

    loadWellFinancialInstrument() {
        this.subscriptions.add(this.wellAssociatedFinancialInstrument$.pipe(
        ).subscribe(
            (instrument: FinancialInstrument) => {
                this.hasFetchedFinancialInstrument = true;
                if (instrument)
                    this._wellFinancialInstrument = instrument;
            },
            error => { console.error(error); console.error("ERROR Fetching the financial Instrument for this well."); }
        ));
    }

    loadWellTypeDescriptionData() {
        //Fetch the well type description domain values, if applicable
        if (this._wellTypeDescriptionDomainId != null) {
            this.subscriptions.add(this.wellTypeDescriptionDomainValues$.pipe(
            ).subscribe(
                (domainValues: DomainValue[]) => {
                    if (domainValues)
                        this.wellTypeDescriptionDomainValues = domainValues;
                },
                error => { console.error(error); console.error("ERROR Fetching the " + this._wellTypeDescriptionDomainId + " domain for this well."); }
            ));
        }
    }

    onBondingUpdated(req: StratigraphicWellBondingRequirement): void {
        if (req.isDeleted == false) {
            this.pendingBondingRequirement = req;
            if (this.well.bondingRequirements == null) this.well.bondingRequirements = [];
            if (this.well.activeBondingRequirement != null) {
                this.well.activeBondingRequirement.effectiveTo = new Date();
            }
            const currentActive = this.well.bondingRequirements.find(b => b.effectiveTo == null && b.id !== 0);
            if (currentActive != null) currentActive.effectiveTo = new Date();
            const currentPending = this.well.bondingRequirements.findIndex(b => b.id == 0);

            if (currentPending != -1) {
                this.well.bondingRequirements.splice(currentPending, 1);
            }
            this.well.bondingRequirements.unshift(req);
            this.well.activeBondingRequirement = req;
            this.formEdited.emit(true);
        } else {
            const existingIndex = this.well.bondingRequirements.findIndex(b => b.id == 0);
            if (existingIndex != -1) this.well.bondingRequirements.splice(existingIndex, 1);
            if (this.well.bondingRequirements.length > 0) {
                this.well.bondingRequirements = this.well.bondingRequirements.sort((a, b) => {
                    if (a.effectiveTo > b.effectiveTo) return 1;
                    if (a.effectiveTo < b.effectiveTo) return -1;
                    return 0;
                });
                this.well.bondingRequirements[0].effectiveTo = null;
                this.well.activeBondingRequirement = this.well.bondingRequirements[0];
            }
        }
      
        
	}

	onTabClicked(tab: TabComponent): void {
		if (tab.title === 'Formations') {
			setTimeout(() => {
				if (this.formationsComponent != null) {
					this.formationsComponent.sizeColumnsToFit();
				}
				if (this.casingsComponent != null) {
					this.casingsComponent.sizeColumnsToFit();
				}
			}, 1000);
		} 
	}

    openEditor(formName: string): void {
        if (this.enableEditing) {
            let form: FormGroup = <FormGroup>this.editorForm.get(formName);
            let controls: DialogControl[] = [];
            switch (formName) {
                case "lease": {
                    controls.push(new DialogControl("leaseName", "Lease Name", form.get("leaseName").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("lease");
                    break;
                }
                case "wellSymbol": {
                    controls.push(new DialogControl("wellSymbol", "Well Symbol", form.get("wellSymbol").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("wellSymbol");
                    break;
                }
                case "acresInDrillingUnit": {
                    controls.push(new DialogControl("acresInDrillingUnit", "Unit Acres", form.get("acresInDrillingUnit").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("acresInDrillingUnit");
                    break;
                }
                case "rigType": {
                    controls.push(new DialogControl("rigType", "Rig Type", form.get("rigType").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("rigType");
                    break;
                }
                case "wellName": {
                    controls.push(new DialogControl("wellName", "Well Name", form.get("wellName").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("wellName");
                    break;
                }
                case "wellNumber": {
                    controls.push(new DialogControl("wellNumber", "Well Number", form.get("wellNumber").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("wellNumber");
                    break;
                }
                case "wellClass": {
                    controls.push(new DialogControl("wellClass", "Well Class", form.get("wellClass").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("wellClass");
                    break;
                }
                case "mapSymbol": {
                    controls.push(new DialogControl("mapSymbol", "Map Symbol", form.get("mapSymbol").value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("mapSymbol");
                    break;
                }
                case "groundElevation": {
                    controls.push(new DialogControl("groundElevation", "Ground Elevation", form.get("groundElevation").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("groundElevation");
                    break;
                }
                case "dtd": {
                    controls.push(new DialogControl("dtd", "DTD", form.get("dtd").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("dtd");
                    break;
                }
                case "targetElevation": {
                    controls.push(new DialogControl("targetElevation", "Target Elevation", form.get("targetElevation").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("targetElevation");
                    break;
                }
                case "kickoff": {
                    controls.push(new DialogControl("kickoff", "Kick Off TVD", form.get("kickoff").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("kickoff");
                    break;
                }
                case "digitalElevationModel": {
                    controls.push(new DialogControl("digitalElevationModel", "DEM Elevation", form.get("digitalElevationModel").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("digitalElevationModel");
                    break;
                }
                case "plugbackDepth": {
                    controls.push(new DialogControl("plugbackDepth", "Plugback TVD", form.get("plugbackDepth").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("plugbackDepth");
                    break;
                }
                case "loggersDepth": {
                    controls.push(new DialogControl("loggersDepth", "LTD", form.get("loggersDepth").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("loggersDepth");
                    break;
                }
                case "trueVerticalDepth": {
                    controls.push(new DialogControl("trueVerticalDepth", "TVD", form.get("trueVerticalDepth").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("trueVerticalDepth");
                    break;
                }
                case "proposedFormation": {
                    controls.push(new DialogControl("proposedFormation", "Production Formation", form.get('proposedFormation').value, DialogControlType.Text, [], []));
                    this.editorDialog.setFormName("proposedFormation");
                    break;
                }
                case "dateIssued": {
                    let date = new Date(form.get('dateIssued').value);
                    date.setHours(date.getHours() + 5); //intended to fix localization, defaults to midnight so we have to add hours to have correct date

                    controls.push(new DialogControl("dateIssued", "Issued", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("dateIssued");
                    break;
                }
                case "aapgBasinCode": {
                    controls.push(new DialogControl("aapgBasinCode", "AAPG Basin Code", form.get("aapgBasinCode").value, DialogControlType.Number, [], []));
                    this.editorDialog.setFormName("aapgBasinCode");
                    break;
                }
                case "spudDate": {
                    let date = new Date(form.get('spudDate').value);
                    date.setHours(date.getHours() + 5);

                    controls.push(new DialogControl("spudDate", "Spud", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("spudDate");
                    break;
                }
                case "dateOfFirstProduction": {
                    let date = new Date(form.get('dateOfFirstProduction').value);
                    date.setHours(date.getHours() + 5);

                    controls.push(new DialogControl("dateOfFirstProduction", "Put Into Production", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("dateOfFirstProduction");
                    break;
                }
                case "dateTotalDepthReached": {
                    let date = new Date(form.get('dateTotalDepthReached').value);
                    date.setHours(date.getHours() + 5);

                    controls.push(new DialogControl("dateTotalDepthReached", "Total Depth Reached", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("dateTotalDepthReached");
                    break;
                }
                case "datePluggingApproved": {
                    let date = new Date(form.get('datePluggingApproved').value);
                    date.setHours(date.getHours() + 5);


                    controls.push(new DialogControl("datePluggingApproved", "Plugging Plan Approved", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("datePluggingApproved");
                    break;
                }
                case "datePluggedAndAbandoned": {
                    let date = new Date(form.get('datePluggedAndAbandoned').value);
                    date.setHours(date.getHours() + 5);


                    controls.push(new DialogControl("datePluggedAndAbandoned", "Plugged And Abandoned", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("datePluggedAndAbandoned");
                    break;
				}
				case "vaultedVented": {
					let options: Option[] = [];
					options.push({ id: "false", text: "No" } as Option);
					options.push({ id: "true", text: "Yes" } as Option);
					controls.push(new DialogControl("vaultedVented", "Vaulted / Vented", form.get('vaultedVented').value, DialogControlType.Dropdown, options, []));	
					this.editorDialog.setFormName("vaultedVented");
					break;
				}
                case "dateOfPlugBack": {
                    let date = new Date(form.get('dateOfPlugBack').value);
                    date.setHours(date.getHours() + 5);
                    controls.push(new DialogControl("dateOfPlugBack", "Plug Back", date, DialogControlType.Date, [], []));
                    this.editorDialog.setFormName("dateOfPlugBack");
                    break;
                }
                
                case "disposition": {
                    let options: Option[] = [];
                    this.wellDispositionDomainValues.forEach(d => {
                        options.push({
                            id: d.Code,
                            text: d.Definition
                        } as Option);
                    });
                    options.unshift({
                        id: "",
                        text: ""
                    } as Option);
                    let dispositionCtrl = new DialogControl("disposition", "Disposition", form.get("disposition").value, DialogControlType.Dropdown, options, [Validators.required]);
                    let trackingOptions: Option[] = [{
                        id: " ",
                        text: ""
                    } as Option];
                    let set = this._dispositionOptions.find(s => s.value === form.get("disposition").value);
                    if (set != null) {
                        trackingOptions = set.set;
                    }
                    let legacyTrackingCtrl = new DialogControl("legacyTracking", "Well Disposition Legacy Tracking", form.get('legacyTracking').value, DialogControlType.Dropdown, trackingOptions, [Validators.required]);
                    dispositionCtrl.addTargettedControl("legacyTracking", this._dispositionOptions);
                    controls.push(dispositionCtrl);
                    controls.push(legacyTrackingCtrl);
                    this.editorDialog.setFormName("disposition");
                    break;
                }
                case "wellType": {
                    let options: Option[] = [];
                    this.wellTypeDomainValues.forEach(d => {
                        options.push({
                            id: d.Code,
                            text: d.Definition
                        } as Option);
                    });
                    options.unshift({
                        id: "",
                        text: ""
                    } as Option);
                    let typeCtrl = new DialogControl("wellType", "Well Type", form.get("wellType").value, DialogControlType.Dropdown, options, [Validators.required]);
                    let descriptionOptions: Option[] = [{
                        id: "",
                        text: ""
                    } as Option];
                    let set = this._typeDescriptionOptions.find(s => s.value === form.get("wellType").value);
                    if (set != null) {
                        descriptionOptions = set.set;
                    }
                    let descriptionCtrl = new DialogControl("wellTypeDescription", "Well Type Description", form.get('wellTypeDescription').value, DialogControlType.Dropdown, descriptionOptions, [Validators.required]);
                    typeCtrl.addTargettedControl("wellTypeDescription", this._typeDescriptionOptions);
                    
                    controls.push(typeCtrl);
                    controls.push(descriptionCtrl);

                    typeCtrl.addTargettedControl("hasReleaseOfLease", this._releaseOfLeaseOptions);
                    typeCtrl.addTargettedControl("has5Acres", this._without5AcresOptions);
                    let fiveAcresCtrl = new DialogControl("has5Acres", "Have Over 5 Acres", form.get('has5Acres').value, DialogControlType.Checkbox, [], []);
                    fiveAcresCtrl.addControlDependency("wellTypeDescription", ["XR", "XB"], PropertyDependency.Visible);
                    fiveAcresCtrl.addControlDependency("wellTypeDescription", ["XR"], PropertyDependency.Enabled);
                    fiveAcresCtrl.addControlDependency("hasReleaseOfLease", [{ whenValueIs: true } as PreventBooleanFromChanging], PropertyDependency.CanToggleCheckbox);
                    let releaseOfLeaseCtrl = new DialogControl("hasReleaseOfLease", "Have Release of Lease", form.get('hasReleaseOfLease').value, DialogControlType.Checkbox, [], []);
                    releaseOfLeaseCtrl.addControlDependency("wellTypeDescription", ["XR", "XB"], PropertyDependency.Visible);
                    releaseOfLeaseCtrl.addControlDependency("wellTypeDescription", ["XR"], PropertyDependency.Enabled);
                    releaseOfLeaseCtrl.addControlDependency("has5Acres", [{ whenValueIs: true } as PreventBooleanFromChanging], PropertyDependency.CanToggleCheckbox );
                    controls.push(fiveAcresCtrl);
                    controls.push(releaseOfLeaseCtrl);
                    descriptionCtrl.addTargettedControl("hasReleaseOfLease", this._releaseOfLeaseOptions);
                    descriptionCtrl.addTargettedControl("has5Acres", this._without5AcresOptions);
                    this.editorDialog.setFormName("wellType");
                    break;
                }
                case "location": {
                    let value = form.get('location').value;
                    controls.push(new DialogControl('location', "Source of Location", value, DialogControlType.Dropdown, this.sourceOfLocationOptions, []));
                    this.editorDialog.setFormName("location");
                    break;
                }
                case "wellHead":
                case "target":
                case "heel":
                case "toe": {
                    let latitude = form.get('coordinates').get('latitude83').value;
                    let longitude = form.get('coordinates').get('longitude83').value;
                    controls.push(new DialogControl('coordinates|latitude83', "Latitude (NAD83)", latitude, DialogControlType.Number, [], [Validators.min(0), Validators.required]));
                    controls.push(new DialogControl('coordinates|longitude83', "Longitude (NAD83)", longitude, DialogControlType.Number, [], [Validators.max(0), Validators.required]));
                    this.editorDialog.setFormName(formName);
                    break;
                }
                    

            }
            controls.push(new DialogControl("comments", "Comments", form.get('comments').value, DialogControlType.TextEditor, [], [Validators.required]));
            this.editorDialog.setControls(controls);
            this.editorDialog.toggleDialog(true);
            this.dialogSvc.updateDialog(this.editorDialog);
        } else {
            this.dialogSvc.showFormNotReady();
        }       
    }

    pointExists(pointType: GISPointTypes): boolean {
        let exists: boolean = this._well.location.gisPoints.findIndex(f => f.pointType == pointType) != -1;
        if (exists == false) {
            let pointForm = this.editorForm.get(pointType);
            let addedCtrl = pointForm.get('pointAdded');
            exists = addedCtrl != null && addedCtrl.value == true;
        }
        return exists;
	}

	
    pointTypeFormatter(pointType: GISPointTypes) {
        switch (pointType) {
            case GISPointTypes.FirstTakePoint: return "First Take Point";
            case GISPointTypes.LastTakePoint: return "Last Take Point";
            case GISPointTypes.Target: return "Target";
            case GISPointTypes.WellHead: return "Well Head";
            case GISPointTypes.Heel: return "Heel";
            case GISPointTypes.Toe: return "Toe";
            default: return "";
        }
    }

    queryGIS(form: FormGroup): Observable<boolean> {
      
        let latitude = form.get('coordinates').get('latitude83').value;
        let longitude = form.get('coordinates').get('longitude83').value;
        let latLong: number[] = [Number(latitude), Number(longitude)];
        let countyQuery: Promise<boolean> = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Counties", latLong)
        let subdivisionQuery: Promise<boolean> = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "land_subdivisions_old", latLong);
        let quadrangleQuery: Promise<boolean> = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "quad24k", latLong);

        let query$: Observable<boolean> = from(countyQuery).pipe(
            map((res: any) => {
                let verified: boolean = false;
                if (res != null && res.features != null && res.features.length > 0) {
                    let attributes = res.features[0].attributes;
                    if (attributes != null) {
                        let countyName = attributes["COUNTY"];
                        form.get('county').get('name').setValue(countyName);
                        let countyNumber = attributes["FIPS"];
                        form.get('county').get('apiNumber').setValue(countyNumber);
                    }
                    verified = true;
                }
                return verified;
            }),
            switchMap((previous: boolean) => {
                return from(subdivisionQuery).pipe(
                    map((res: any) => {
                        let verified: boolean = false;
                        if (res != null && res.features != null && res.features.length > 0) {
                            let attributes = res.features[0].attributes;
                            if (attributes != null) {
                                let allotment = attributes["ALLOTMENT"];
                                form.get("allotment").setValue(allotment);
                                let civilTownshipNumber = attributes["CVLTWP_NO"];
                                form.get("civilTownshipNumber").setValue(civilTownshipNumber);
                                let tract = attributes["TRACT"];
                                form.get("tract").setValue(tract);
                                let quarterTownship = attributes["QTR_TWP"];
                                form.get("quarterTownship").setValue(quarterTownship);
                                let rangeDirection = attributes["RNG_DIR"];
                                form.get("range").get("direction").setValue(rangeDirection);
                                let rangeNumber = attributes["RNG_NUM"];
                                form.get("range").get("number").setValue(rangeNumber);
                                let section = attributes["SECTION_"];
                                form.get("section").setValue(section);
                                let township = attributes["TOWNSHIP"];
                                form.get("township").get("name").setValue(township);
                                let townshipDirection = attributes["TWP_DIR"];
                                form.get("township").get("direction").setValue(townshipDirection);
                                let fraction = attributes["FRACTION"];
                                form.get("fraction").setValue(fraction);
                                let lot = attributes["LOT"];
                                form.get("lot").setValue(lot);
                            }
                            verified = true;
                        }
                        return previous || verified;
                    }));
            }),
            switchMap((previous: boolean) => {
                return from(quadrangleQuery).pipe(
                    map((res: any) => {
                        let verified: boolean = false;
                        if (res != null && res.features != null && res.features.length > 0) {
                            let attributes = res.features[0].attributes;
                            if (attributes != null) {
                                let code = attributes["OGSCODE"];
                                form.get('ogsQuadrangle').get('code').setValue(code);
                                let ogsName = attributes["QUADNAME"];
                                form.get('ogsQuadrangle').get('name').setValue(ogsName);
                            }
                        }
                        verified = true;
                        return verified || previous;
                    })

                )
            })
        )

        return query$;
    }

    saveUpdate(args: any): void {
        let formName = args.name;
        let form: FormGroup = <FormGroup>args.form;
		let existing = <FormGroup>this.editorForm.get(formName);
        if (existing != null && existing.controls != null) {
            Object.keys(form.controls).forEach(updateKey => {
                let keyParts = updateKey.split('|');
                let origin: AbstractControl = null;
                if (keyParts.length > 0) {
                    origin = existing.get(keyParts[0]);
                    for (let k = 1; k < keyParts.length; k++) {
                        origin = origin.get(keyParts[k]);
                    }
                }
                if (origin != null) {
                    let changed: boolean = false;
                    let update = form.get(updateKey);
                    if (update != null) {
                        changed = origin.value !== update.value;
                        if (changed) {
                            origin.setValue(update.value);
                            origin.markAsDirty();
                            this.formEdited.emit(true);
                        }
                    }
                    
                }
            });           
        }
        if (this.validPointTypes.includes(formName)) {
            //a point was updated, run a GIS query using the provided coordinates
            let pointForm = <FormGroup>this.editorForm.get(formName);
            let obs$ = this.queryGIS(pointForm);
            this.loader.toggleLoader(true);
            let subs$ = obs$.subscribe(
                (verified: boolean) => {
                    this.loader.toggleLoader(false);
                    pointForm.get("gisQueryRun").setValue(verified);
                    if (verified) {
                        form.markAsDirty();
                    }
                },
                (error) => {
                    this.loader.toggleLoader(false);
                    console.error(error);
                    this.dialogSvc.showError();
                }
            );
            this.subscriptions.add(subs$);
        }
        
    }

    setDomesticWellFlags() {
        if (this._well) {
            //For it to be XO, need to set permitting modifiers as last transferred prior to 2010
            if (this._well.wellStatus.type.description == "XO") {
                //If they aren't marked as last transferred prior to 2010, do so
                if (!((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.LastTransferPriorToJune2010) > 0))
                    this._well.wellStatus.permittingModifiers += WellPermittingModifiers.LastTransferPriorToJune2010;
            }
            else if (this._well.wellStatus.type.description == "XB" || this._well.wellStatus.type.description == "XR") {
                //If it's non XO domestic, need to remove the last transferred prior to 2010 flag
                if (((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.LastTransferPriorToJune2010) > 0))
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.LastTransferPriorToJune2010;
            }

            //For it to be XB, need to set permitting modifiers as having more than 5 acres and a release of lease
            if (this._well.wellStatus.type.description == "XB") {
                //If they aren't marked as having a release of lease, do so
                if (!((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) > 0))
                    this._well.wellStatus.permittingModifiers += WellPermittingModifiers.ReleaseOfLease;

                //if they are marked as lacking 5 acres, undo that
                if ((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) > 0)
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.WithoutFiveAcres;
            }

            //IF they set status to XR, and well has the release of lease flag set to to true and the WithoutFiveAcres flag set to false, set the release of lease to false
            //as XR means they don't have a release of lease, and/or they are without 5 acres
            if (this._well.wellStatus.type.description == "XR") {
                //If they aren't marked as having a release of lease, do so
                if (((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) > 0)
                    && !((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) > 0))
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease;

            }

            let domesticTypeDescriptions = ["XB", "XR", "XO"];

            //If the type description is changed to not be a domestic type at all, remove both flags and domestic status
            if (!(domesticTypeDescriptions.filter(a => a == this._well.wellStatus.type.description).length > 0)) {
                if ((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) > 0)
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease;
                if ((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) > 0)
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.WithoutFiveAcres;
                if ((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ExemptDomestic) > 0)
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.ExemptDomestic;
            }
            //However, if it's changed to a domestic one, make sure the domestic flag is set
            else {
                if (!((this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ExemptDomestic) > 0))
                    this._well.wellStatus.permittingModifiers += WellPermittingModifiers.ExemptDomestic;
            }

            this.hasReleaseOfLease = (this._well.wellStatus.permittingModifiers & WellPermittingModifiers.ReleaseOfLease) > 0;
            this.isWithout5Acres = (this._well.wellStatus.permittingModifiers & WellPermittingModifiers.WithoutFiveAcres) > 0;
        }
    }

    setStatusDispositionLegacyTracking(code: string) {
        this.well.wellStatus.legacyTracking = code;
    }

    setStatusTypeDescription(code: string) {
        this.well.wellStatus.type.description = code;

        this.setDomesticWellFlags();
    }

    toggleReleaseOfLease(e?: MouseEvent) {
        let stopEvent = true;

        if (this._well) {
            //Items can only be toggled when description is set to XR
            if (this._well.wellStatus.type.description == "XR") {
                //If they currently have a release of lease remove it
                if (this.hasReleaseOfLease) {
                    this._well.wellStatus.permittingModifiers -= WellPermittingModifiers.ReleaseOfLease
                    this.hasReleaseOfLease = !this.hasReleaseOfLease;
                    stopEvent = false;
                }
                //If XR can only add release of lease if they are without 5 acres
                else if (this.isWithout5Acres) {
                    this._well.wellStatus.permittingModifiers += WellPermittingModifiers.ReleaseOfLease
                    this.hasReleaseOfLease = !this.hasReleaseOfLease;
                    stopEvent = false;
                }
            }
        }
        else
            stopEvent = true;

        if (event && stopEvent) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    }

    toggleIsWithout5Acres() {
        let stopEvent = true;

        if (this._well) {
            //Items can only be toggled when description is set to XR
            if (this._well.wellStatus.type.description == "XR") {
                //If XR can only remove without 5 acres if they are without release of lease
                if (this.isWithout5Acres && !this.hasReleaseOfLease) {
                    this._well.wellStatus.permittingModifiers = this._well.wellStatus.permittingModifiers - WellPermittingModifiers.WithoutFiveAcres
                    this.isWithout5Acres = !this.isWithout5Acres;
                    stopEvent = false;
                }
                else if (!this.isWithout5Acres) {
                    this._well.wellStatus.permittingModifiers = this._well.wellStatus.permittingModifiers + WellPermittingModifiers.WithoutFiveAcres
                    this.isWithout5Acres = !this.isWithout5Acres;
                    stopEvent = false;
                }
            }
        }

        if (event && stopEvent) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
	}

	updateCasingTubingRecord(modified: EditedCasingTubingRecord): void {
		let exists = this.modifiedCasingRecords.findIndex(f => f.id == modified.id);
		if (exists != -1) {
			this.modifiedCasingRecords.splice(exists, 1);
		}
		this.modifiedCasingRecords.push(modified);
		this.casingRecordUpdated.emit(modified);
	}

	updateFormationRecord(modified: EditedFormationRecord): void {
		let exists = this.modifiedFormationRecords.findIndex(f => f.id == modified.id);
		if (exists != -1) {
			this.modifiedFormationRecords.splice(exists, 1);
		}
		this.modifiedFormationRecords.push(modified);
		this.formationRecordUpdated.emit(modified);
	}
}
